import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import ReactTableTrademo from "@shared/trademo-datatable/react-table-trademo";
import Axios from "axios";
import ReactCountryFlag from "react-country-flag";
import { unifiedIsoCountries } from "@core/dictionary/global";
import { getIntelUrl } from "@utils/intelUrl";
import ReactTooltip from "react-tooltip";
import DownloadJustification from "./downloadJustification";
import ClassificationTypeFilter from "./classificationTypeFilter";
import styled from "styled-components";
import { getApiUrl } from "@utils/baseUrl";
import CustomToast from "./customToast";
import { TriggerSearch, UserAvatar } from "./utils";
import ClassificationFilter from "./classificationFilter";
import * as Sentry from "@sentry/browser";
import ActionsMenu from "./actionsMenu";

const CustomStylesClassificationTable = styled.div`
  margin-bottom: 0px !important;
  font-family: "Inter", sans-serif;
  .new-trademo-table-container {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    min-height: 30rem;
    border-radius: 8px;
    ::-webkit-scrollbar {
      height: 6px;
    }
  }
  table {
    border-spacing: 0;
    font-weight: 500;
    .datatable-checkbox {
      font-size: 18px;
      cursor: pointer;
      padding: 0px;
      margin-top: -1px;
      vertical-align: middle;
      position: relative;
      :after {
        left: -1.05px;
      }
    }
    thead {
      font-size: 12px !important;
      border: 0;
      border-top: none;
      background-color: #f2f4f7;
      color: #101828;
      border-bottom: 1px solid #eaecf0;
      th {
        border-right: ${(props) => (props.hideBorder ? "none" : " 1px solid #eaecf0")};
        input {
          margin-bottom: 4px;
        }
        span {
          margin: auto 0px;
        }
        font-weight: 600;
        box-sizing: border-box;
        height: 40px;
        vertical-align: middle !important;
        border-top: none;
        background-color: #f2f4f7;
        .form-check {
          padding-left: 5px !important;
          position: relative;
          .form-check-input {
            position: absolute;
            margin-left: 0 !important;
            margin-top: 5px !important;
          }
        }
        &:last-child {
          border-right: none;
          position: sticky;
          right: 0;
          background-color: #f2f4f7;
          z-index: 3;
          box-shadow: -2px 0px 5px rgba(0, 0, 0, 0.1);
        }
      }
    }
    tbody {
      tr {
        td {
          height: 48px;
          vertical-align: middle;
          color: rgba(0, 0, 0, 0.87) !important;
          font-weight: 400 !important;
          border-right: 1px solid #eaecf0;
          border-right: ${(props) => (props.hideBorder ? "none" : " 1px solid #eaecf0")};
          &:last-child {
            border-right: none;
            position: sticky;
            right: 0;
            background-color: #ffffff;
            z-index: 1;
            box-shadow: -2px 0px 5px rgba(0, 0, 0, 0.1); /* Shadow to the left */
          }
        }
        svg {
          width: 18px;
          height: 18px;
          stroke: #667085;
          stroke-width: 2;
        }
        :hover {
          background-color: #f8f6fe !important;
        }
        &:last-child td {
          border-bottom: 1px solid #eaecf0 !important;
        }
      }
    }
    tr {
      padding: 15px;
      font-size: 12px !important;
    }
    th,
    td {
      margin: 0;
      padding: 1rem;
      border: ${(props) => (props.hideBorder ? "none" : " 1px solid #eaecf0")};
      border-bottom: ${(props) => (props.hideBorder ? " 1px solid #eaecf0" : "none")};
      border-left-style: none;
    }
  }
  .pagination {
    padding: 0.5rem;
  }
`;

const ClassifiedProducts = ({ setSelectedRows, clearSelection, loading, setLoading, setRemainingValueAddCredits }) => {
  const [rowPerPage, setRowPerPage] = useState(30);
  const [pageNumber, setPageNumber] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(-1);
  const [sortDirection, setSortDirection] = useState("desc");
  const [availableClassificationTypes, setAvailableClassificationTypes] = useState([]);
  const [selectedClassificationTypes, setSelectedClassificationTypes] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [availableClassifiedBy, setAvailableClassifiedBy] = useState([]);
  const [selectedClassifiedBy, setSelectedClassifiedBy] = useState([]);
  const [userMapping, setUserMapping] = useState({});
  const [availableHsCodeValues, setAvailableHsCodeValues] = useState([]);
  const [selectedHsCodeValues, setSelectedHsCodeValues] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showHsFilterModal, setShowHsFilterModal] = useState(false);
  const [searchedKeyword, setSearchedKeyword] = useState({ searched: "", entered: "" });
  const filterRef = useRef(null);
  const hsFilterRef = useRef(null);

  const appliedFilters = selectedClassificationTypes.length > 0 || selectedCountries.length > 0 || selectedClassifiedBy.length > 0 || selectedHsCodeValues.length > 0;
  const hasSearched = searchedKeyword?.searched;

  const handleSortChange = () => {
    setSortDirection((prevDirection) => {
      const newDirection = prevDirection === "desc" ? "asc" : "desc";
      getAllProducts(false, newDirection);
      return newDirection;
    });
  };

  const reloadClassifiedSearch = (row) => {
    const tradeDirection = row?.classification_type?.toLowerCase() === "export" ? "import" : "export";
    const hscode = (row?.hscode || "").slice(0, 6);
    const params = new URLSearchParams({ hsCode: hscode, tradeDirection, fromClassifier: "true" });
    window.open(getIntelUrl() + "product-search?" + params.toString(), "_blank");
  };

  const handleClassificationTypeFilter = (index) => {
    const filterData = availableClassificationTypes.map((item, i) => ({
      ...item,
      isChecked: i === index ? !item.isChecked : item.isChecked,
    }));
    setAvailableClassificationTypes(filterData);

    const selectedTypes = filterData.filter((item) => item.isChecked).map((item) => item.name);
    setSelectedClassificationTypes(selectedTypes);
  };

  const handleHsCodeFilter = (index) => {
    const filterData = availableHsCodeValues.map((item, i) => ({
      ...item,
      isChecked: i === index ? !item.isChecked : item.isChecked,
    }));
    setAvailableHsCodeValues(filterData);

    const selectedHsCodes = filterData.filter((item) => item.isChecked).map((item) => item.name);
    setSelectedHsCodeValues(selectedHsCodes);
  };

  const handleCountryFilter = (selected) => {
    setSelectedCountries(selected);
  };

  const handleClassifiedByFilter = (selected) => {
    setSelectedClassifiedBy(selected);
  };

  const clearStatusFilter = () => {
    setSelectedClassificationTypes([]);
    const clearedFilterData = availableClassificationTypes.map((item) => ({
      ...item,
      isChecked: false,
    }));
    setAvailableClassificationTypes(clearedFilterData);
    setShowFilterModal(false);
  };

  const clearHsFilter = () => {
    setSelectedHsCodeValues([]);
    const clearedFilterData = availableHsCodeValues.map((item) => ({
      ...item,
      isChecked: false,
    }));
    setAvailableHsCodeValues(clearedFilterData);
    setShowHsFilterModal(false);
  };

  const clearCountryFilter = () => {
    setSelectedCountries([]);
    setAvailableCountries(
      availableCountries.map((item) => ({
        ...item,
        isChecked: false,
      }))
    );
  };

  const clearClassifiedByFilter = () => {
    setSelectedClassifiedBy([]);
    setAvailableClassifiedBy(
      availableClassifiedBy.map((item) => ({
        ...item,
        isChecked: false,
      }))
    );
  };

  const handleFilterClick = (e) => {
    e.stopPropagation();
    setShowFilterModal(!showFilterModal);
  };

  const handleHsFilterClick = (e) => {
    e.stopPropagation();
    setShowHsFilterModal(!showHsFilterModal);
  };

  const classificationTypeFilterCount = selectedClassificationTypes.length;
  const countryFilterCount = selectedCountries.length;
  const classifiedByFilterCount = selectedClassifiedBy.length;
  const hsCodeFilterCount = selectedHsCodeValues.length;

  const columns = useMemo(() => {
    let column = [
      {
        name: "Product Title",
        selector: (row) => (
          <div>
            <span className="tw-line-clamp-2" data-for={`title-${row?.id}`} data-tip={row?.title} data-event="click" data-event-off="mouseleave">
              {row?.title}
            </span>
            {row?.title?.length > 30 && <ReactTooltip className="trademo-tooltip" id={`title-${row?.id}`} place="right" />}
          </div>
        ),
        miWidth: "150px",
      },
      {
        name: "Description",
        selector: (row) => (
          <div>
            <span className="tw-line-clamp-2" data-for={`desc-${row?.id}`} data-tip={row?.description} data-event="click" data-event-off="mouseleave">
              {row?.description}
            </span>
            {row?.description?.length > 45 && <ReactTooltip className="trademo-tooltip" id={`desc-${row?.id}`} place="right" />}
          </div>
        ),
        miWidth: "150px",
      },
      {
        name: (
          <div ref={filterRef} className="cursor-pointer tw-relative tw-flex tw-gap-2 tw-items-center" onClick={handleFilterClick}>
            Classification Type
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1c1b1f">
              <path d="M456.18-192Q446-192 439-198.9t-7-17.1v-227L197-729q-9-12-2.74-25.5Q200.51-768 216-768h528q15.49 0 21.74 13.5Q772-741 763-729L528-443v227q0 10.2-6.88 17.1-6.89 6.9-17.06 6.9h-47.88ZM480-498l162-198H317l163 198Zm0 0Z" />
            </svg>
            {classificationTypeFilterCount > 0 && (
              <span className="green-badge">
                <span>{classificationTypeFilterCount}</span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearStatusFilter();
                  }}
                  className="pl-1">
                  X
                </span>
              </span>
            )}
            {showFilterModal && (
              <div className="tw-absolute tw-z-50 tw-top-full tw-mt-1 tw-border tw-border-[#e7e7e7] tw-bg-white tw-shadow-md tw-w-40" ref={filterRef}>
                <ClassificationTypeFilter isDownload={true} onChangeHandler={handleClassificationTypeFilter} data={availableClassificationTypes} />
              </div>
            )}
          </div>
        ),
        selector: (row) => <span className="tw-capitalize">{row?.classification_type}</span>,
        miWidth: "220px",
      },
      {
        name: (
          <div className="cursor-pointer tw-relative tw-flex tw-gap-2 tw-items-center">
            <ClassificationFilter options={availableCountries.map((item) => item.name)} selectedFilters={selectedCountries} onFilterChange={handleCountryFilter} isCountry={true} placeholder="Search countries..." />
            {countryFilterCount > 0 && (
              <span className="green-badge">
                <span>{countryFilterCount}</span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearCountryFilter();
                  }}
                  className="pl-1">
                  X
                </span>
              </span>
            )}
          </div>
        ),
        selector: (row) => (
          <div className="tw-flex tw-gap-1 tw-items-center">
            <ReactCountryFlag countryCode={row?.country} svg style={{ width: "1.5em", height: "1.5em" }} />
            {unifiedIsoCountries[row?.country]}
          </div>
        ),
        miWidth: "200px",
      },
      {
        name: (
          <div ref={hsFilterRef} className="cursor-pointer tw-relative tw-flex tw-gap-2 tw-items-center" onClick={handleHsFilterClick}>
            HS Code
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1c1b1f">
              <path d="M456.18-192Q446-192 439-198.9t-7-17.1v-227L197-729q-9-12-2.74-25.5Q200.51-768 216-768h528q15.49 0 21.74 13.5Q772-741 763-729L528-443v227q0 10.2-6.88 17.1-6.89 6.9-17.06 6.9h-47.88ZM480-498l162-198H317l163 198Zm0 0Z" />
            </svg>
            {hsCodeFilterCount > 0 && (
              <span className="green-badge">
                <span>{hsCodeFilterCount}</span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearHsFilter();
                  }}
                  className="pl-1">
                  X
                </span>
              </span>
            )}
            {showHsFilterModal && (
              <div className="tw-absolute tw-z-50 tw-top-full tw-mt-1 tw-border tw-border-[#e7e7e7] tw-bg-white tw-shadow-md tw-w-40">
                <ClassificationTypeFilter isDownload={true} onChangeHandler={handleHsCodeFilter} data={availableHsCodeValues} />
              </div>
            )}
          </div>
        ),
        selector: (row) => {
          const hscode = row?.hscode;
          if (hscode === "failed") {
            return (
              <span className="tw-capitalize tw-text-gray-900 tw-flex tw-items-center tw-gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#B42318">
                  <path d="m48-144 432-720 432 720H48Zm127-72h610L480-724 175-216Zm304.79-48q15.21 0 25.71-10.29t10.5-25.5q0-15.21-10.29-25.71t-25.5-10.5q-15.21 0-25.71 10.29t-10.5 25.5q0 15.21 10.29 25.71t25.5 10.5ZM444-384h72v-192h-72v192Zm36-86Z" />
                </svg>
                <span>Failed</span>
              </span>
            );
          } else if (hscode === "pending") {
            return (
              <span className="tw-capitalize tw-text-gray-900 tw-flex tw-items-center tw-gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#f3e781">
                  <path d="M522-80v-82q34-5 66.5-18t61.5-34l56 58q-42 32-88 51.5T522-80Zm-80 0Q304-98 213-199.5T122-438q0-75 28.5-140.5t77-114q48.5-48.5 114-77T482-798h6l-62-62 56-58 160 160-160 160-56-56 64-64h-8q-117 0-198.5 81.5T202-438q0 104 68 182.5T442-162v82Zm322-134-58-56q21-29 34-61.5t18-66.5h82q-5 50-24.5 96T764-214Zm76-264h-82q-5-34-18-66.5T706-606l58-56q32 39 51 86t25 98Z" />
                </svg>
                <span>Pending</span>
              </span>
            );
          } else {
            return <span className="tw-capitalize tw-text-gray-900">{hscode}</span>;
          }
        },
        miWidth: "170px",
      },
      {
        name: (
          <div className="cursor-pointer tw-relative tw-flex tw-gap-2 tw-items-center">
            <ClassificationFilter options={availableClassifiedBy.map((item) => item.name)} selectedFilters={selectedClassifiedBy} onFilterChange={handleClassifiedByFilter} isCountry={false} placeholder="Search users..." />
            {classifiedByFilterCount > 0 && (
              <span className="green-badge">
                <span>{classifiedByFilterCount}</span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearClassifiedByFilter();
                  }}
                  className="pl-1">
                  X
                </span>
              </span>
            )}
          </div>
        ),
        selector: (row) => <UserAvatar name={row?.classified_by} className="!tw-size-6 !tw-text-[10px]" />,
        miWidth: "200px",
      },
      {
        name: (
          <div className="tw-flex tw-gap-2 tw-items-center">
            <span>Classified On</span>
            <div className="tw-cursor-pointer" onClick={handleSortChange}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1c1b1f">
                <path d="M320-440v-287L217-624l-57-56 200-200 200 200-57 56-103-103v287h-80ZM600-80 400-280l57-56 103 103v-287h80v287l103-103 57 56L600-80Z" />
              </svg>
            </div>
          </div>
        ),
        selector: (row) => row?.classified_on,
        miWidth: "170px",
      },
      {
        name: "Justification Report",
        selector: (row) => <DownloadJustification rowData={row} disabled={row?.hscode === "pending" || row?.hscode === "failed"} refreshTable={getAllProducts} />,
        miWidth: "150px",
      },
      {
        name: "Actions",
        selector: (row) => <ActionsMenu rowData={row} reloadClassifiedSearch={reloadClassifiedSearch} />,
        miWidth: "50px",
      },
    ];

    return column;
  }, [pageNumber, rowPerPage, totalDataCount, showFilterModal, showHsFilterModal, availableClassificationTypes, availableHsCodeValues, selectedClassificationTypes, selectedHsCodeValues, hsCodeFilterCount]);

  const getAllProducts = async (isPagination = false, sortDir = sortDirection) => {
    setLoading(true);
    try {
      const classifiedByUserIds = selectedClassifiedBy.map((userName) => {
        const userId = Object.keys(userMapping).find((id) => userMapping[id] === userName);
        return userId || userName;
      });
      const requestPayload = {
        classification_type: selectedClassificationTypes,
        country: selectedCountries,
        sort: sortDir,
        from: isPagination ? tableData.length : 0,
        classified_by: classifiedByUserIds,
        search: searchedKeyword?.entered,
        hscode: selectedHsCodeValues,
      };
      const response = await Axios.post(getApiUrl("classifier/logs"), requestPayload);
      const { data: { table_data = [], total_count = 0, classification_type = [], countries = [], hscode = [], classified_by = [], user_mapping = {}, remaining_value_add_credit = 0 } = {} } = response || {};
      if (isPagination) {
        setTableData((prev) => [...prev, ...table_data]);
      } else {
        setTableData(table_data);
      }
      setTotalDataCount(total_count);
      setRemainingValueAddCredits(remaining_value_add_credit);
      if (classification_type.length > 0 && availableClassificationTypes.length === 0) {
        const filterOptions = classification_type.map((type) => ({
          name: type,
          isChecked: selectedClassificationTypes.includes(type),
        }));
        setAvailableClassificationTypes(filterOptions);
      }
      if (hscode.length > 0 && availableHsCodeValues.length === 0) {
        const filterOptions = hscode.map((type) => ({
          name: type,
          isChecked: selectedHsCodeValues.includes(type),
        }));
        setAvailableHsCodeValues(filterOptions);
      }
      if (countries.length > 0 && availableCountries.length === 0) {
        const countryOptions = countries.map((country) => ({
          name: country,
          isChecked: selectedCountries.includes(country),
        }));
        setAvailableCountries(countryOptions);
      }
      if (classified_by.length > 0 && availableClassifiedBy.length === 0) {
        const classifiedByOptions = classified_by.map((userId) => ({
          id: userId,
          name: user_mapping[userId] || userId,
          isChecked: selectedClassifiedBy.includes(user_mapping[userId] || userId),
        }));
        setAvailableClassifiedBy(classifiedByOptions);
      }
      if (Object.keys(user_mapping).length > 0) {
        setUserMapping(user_mapping);
      }
    } catch (error) {
      Sentry.captureException(error);
      const { response: { data: { error: errorMessage = "Something Went Wrong!!!" } = {} } = {} } = error || {};
      toast(<CustomToast title={errorMessage} customId="error" />, {
        autoClose: 5000,
        draggable: false,
        closeButton: false,
        className: "!tw-bg-gray-900 !tw-overflow-visible !tw-w-[390px] !tw-rounded-2xl !tw-right-20",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTrademoTableSearchPerRowsChange = (data) => {
    setRowPerPage(data);
  };

  const handleSelectedRowsChange = (rows) => {
    setSelectedRows(rows);
  };

  const updateTrademoTableObject = (data) => {
    setRowPerPage(data.perPage);
    setPageNumber(data.currentPage + 1);
  };

  const handleTrademoTablePageChange = (page, perPage) => {
    setRowPerPage(perPage);
    setPageNumber(page);

    if (page * perPage >= tableData.length - rowPerPage && tableData.length < totalDataCount) {
      getAllProducts(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hsFilterRef.current && !hsFilterRef.current.contains(event.target)) {
        setShowHsFilterModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    getAllProducts();
  }, [selectedClassificationTypes, selectedHsCodeValues, selectedCountries, selectedClassifiedBy, searchedKeyword?.searched]);

  const clearAllFilters = () => {
    setSelectedClassificationTypes([]);
    const clearedClassificationTypes = availableClassificationTypes.map((item) => ({
      ...item,
      isChecked: false,
    }));
    setAvailableClassificationTypes(clearedClassificationTypes);
    setSelectedHsCodeValues([]);
    const clearedHsCodeValues = availableHsCodeValues.map((item) => ({
      ...item,
      isChecked: false,
    }));
    setAvailableHsCodeValues(clearedHsCodeValues);
    setSelectedCountries([]);
    const clearedCountries = availableCountries.map((item) => ({
      ...item,
      isChecked: false,
    }));
    setAvailableCountries(clearedCountries);
    setSelectedClassifiedBy([]);
    const clearedClassifiedBy = availableClassifiedBy.map((item) => ({
      ...item,
      isChecked: false,
    }));
    setAvailableClassifiedBy(clearedClassifiedBy);
    setShowFilterModal(false);
    setShowHsFilterModal(false);
  };

  const handleClearSearch = () => {
    setSearchedKeyword({ entered: "", searched: "" });
  };

  const handleInputChange = (value) => {
    if (value === "" && searchedKeyword.searched) {
      handleClearSearch();
    } else {
      setSearchedKeyword((prev) => ({ ...prev, entered: value }));
    }
  };

  const handleSubmit = () => {
    setSearchedKeyword((prev) => ({ ...prev, searched: prev.entered }));
    setPageNumber(1);
  };

  return (
    <div className={`tw-flex tw-flex-col tw-gap-4`}>
      {(tableData?.length > 0 || searchedKeyword?.searched) && (
        <div className="tw-w-[40%] tw-pt-4">
          <TriggerSearch value={searchedKeyword?.entered || ""} onChange={(value) => handleInputChange(value)} onSubmit={handleSubmit} onClear={handleClearSearch} disabled={loading} placeholder="Search by product title or description" />
        </div>
      )}
      {!(tableData?.length > 0 || searchedKeyword?.searched) && appliedFilters && <div className="tw-pt-4"></div>}
      {tableData?.length >= 0 && (
        <div className={`${tableData?.length || (appliedFilters && !tableData?.length) ? "tw-border tw-border-[#e7e7e7]" : ""} tw-rounded-2xl tw-overflow-hidden`}>
          <ReactTableTrademo columns={columns} data={tableData} paginationTotalRows={totalDataCount} onSelectedRowsChange={handleSelectedRowsChange} paginationPerPage={rowPerPage} paginationDefaultPage={pageNumber} selectedData={[]} onChangeRowsPerPage={(e) => handleTrademoTableSearchPerRowsChange(e)} onTableObjectChange={(e) => updateTrademoTableObject(e)} onChangePage={handleTrademoTablePageChange} hideSelection={appliedFilters && !tableData?.length} clearFilter={clearAllFilters} clearSelectedRows={clearSelection} onSort={handleSortChange} hideSorting={true} StyledComponent={CustomStylesClassificationTable} />
        </div>
      )}

      {!loading && hasSearched && !appliedFilters && !tableData?.length && (
        <div className="tw-w-full tw-h-[calc(100vh-210px)] tw-flex tw-items-center tw-justify-center">
          <div className="tw-border-none">
            <div className="tw-h-full tw-flex tw-items-center tw-justify-center tw-flex-col">
              <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1677061659/intel/icons/filternotfound_illustration_xmewxn.svg" alt="empty" />
              <div className="emptystate-content py-4">No data found based on your search</div>
            </div>
          </div>
        </div>
      )}

      {!loading && !appliedFilters && !hasSearched && !tableData?.length && (
        <div className="tw-w-full tw-h-[calc(100vh-160px)] tw-flex tw-items-center tw-justify-center">
          <div className="tw-flex tw-flex-col tw-max-w-[50%] tw-items-center tw-text-center tw-gap-1">
            <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1684997941/intel/icons/emptystate_norequest_b1wzx6.svg" />
            <p>No products classified yet. Once you perform HS classification, they'll appear here</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassifiedProducts;
